import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_THEME, PRESET_THEMES } from "@/data/mock";
import {
  fetchCategories, fetchSettings,
  authMe, authLogin, authRegister, authLogout, authUpdateMe,
  authGoogleSession,
  getFavorites, addFavorite, removeFavorite,
  setUserToken, setAdminToken, getAdminToken,
  adminLoginApi, adminMe,
} from "@/lib/api";

const AppContext = createContext(null);

function applyTheme(t) {
  const root = document.documentElement;
  if (!t) return;
  const map = {
    "--amd-primary": t.primary, "--amd-primary-hover": t.primaryHover,
    "--amd-bg": t.background, "--amd-surface": t.surface, "--amd-surface-alt": t.surfaceAlt,
    "--amd-border": t.border, "--amd-text": t.text, "--amd-text-muted": t.textMuted,
    "--amd-accent": t.accent, "--amd-header-bg": t.headerBg,
  };
  Object.entries(map).forEach(([k, v]) => { if (v) root.style.setProperty(k, v); });
}

export function AppProvider({ children }) {
  const [theme, setThemeState] = useState(DEFAULT_THEME);
  const [moderationEnabled, setModerationEnabled] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingPublic, setLoadingPublic] = useState(true);

  // Auth
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Admin
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);

  // Favorites - stored as id list. Source: backend if logged in, localStorage otherwise.
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("amd64_favs") || "[]"); } catch { return []; }
  });

  const refreshPublic = useCallback(async () => {
    setLoadingPublic(true);
    try {
      const [cats, settings] = await Promise.all([fetchCategories(), fetchSettings()]);
      setCategories(cats || []);
      if (settings?.theme) { setThemeState(settings.theme); applyTheme(settings.theme); }
      if (typeof settings?.moderation_enabled === "boolean") setModerationEnabled(settings.moderation_enabled);
    } finally {
      setLoadingPublic(false);
    }
  }, []);

  const refreshFavorites = useCallback(async (isLogged) => {
    if (isLogged) {
      try {
        const data = await getFavorites();
        setFavorites(data.ids || []);
        localStorage.setItem("amd64_favs", JSON.stringify(data.ids || []));
      } catch (_) { /* ignore */ }
    } else {
      try { setFavorites(JSON.parse(localStorage.getItem("amd64_favs") || "[]")); } catch { setFavorites([]); }
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    setAuthLoading(true);
    try {
      // skip auth check during Google callback (#session_id=...)
      if (window.location.hash?.includes("session_id=")) {
        setAuthLoading(false);
        return;
      }
      const me = await authMe();
      setUser(me);
      await refreshFavorites(true);
    } catch (_) {
      setUser(null);
      await refreshFavorites(false);
    } finally {
      setAuthLoading(false);
    }
  }, [refreshFavorites]);

  const refreshAdmin = useCallback(async () => {
    if (!getAdminToken()) { setAdminLoggedIn(false); return; }
    try { await adminMe(); setAdminLoggedIn(true); }
    catch { setAdminToken(""); setAdminLoggedIn(false); }
  }, []);

  useEffect(() => {
    applyTheme(theme);
    refreshPublic();
    refreshAuth();
    refreshAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Auth actions ----
  const login = useCallback(async (email, password) => {
    const data = await authLogin({ email, password });
    if (data.access_token) setUserToken(data.access_token);
    setUser(data.user);
    await refreshFavorites(true);
    return data.user;
  }, [refreshFavorites]);

  const register = useCallback(async (payload) => {
    const data = await authRegister(payload);
    if (data.access_token) setUserToken(data.access_token);
    setUser(data.user);
    await refreshFavorites(true);
    return data.user;
  }, [refreshFavorites]);

  const completeGoogleLogin = useCallback(async (sessionId) => {
    const data = await authGoogleSession(sessionId);
    setUser(data.user);
    await refreshFavorites(true);
    return data.user;
  }, [refreshFavorites]);

  const logout = useCallback(async () => {
    try { await authLogout(); } catch (_) { /* ignore */ }
    setUserToken("");
    setUser(null);
    await refreshFavorites(false);
  }, [refreshFavorites]);

  const updateProfile = useCallback(async (patch) => {
    const updated = await authUpdateMe(patch);
    setUser(updated);
    return updated;
  }, []);

  // ---- Admin actions ----
  const adminLogin = useCallback(async (username, password) => {
    const data = await adminLoginApi({ username, password });
    setAdminToken(data.admin_token);
    setAdminLoggedIn(true);
    return data;
  }, []);
  const adminLogout = useCallback(() => { setAdminToken(""); setAdminLoggedIn(false); }, []);

  // ---- Favorites ----
  const isFav = useCallback((id) => favorites.includes(id), [favorites]);
  const toggleFavorite = useCallback(async (id) => {
    const willAdd = !favorites.includes(id);
    const next = willAdd ? [...favorites, id] : favorites.filter((x) => x !== id);
    setFavorites(next);
    localStorage.setItem("amd64_favs", JSON.stringify(next));
    if (user) {
      try { if (willAdd) await addFavorite(id); else await removeFavorite(id); }
      catch (_) { /* keep local */ }
    }
  }, [favorites, user]);

  // ---- Theme (only admin uses setTheme via API; preview from public refresh) ----
  const setTheme = useCallback((t) => { setThemeState(t); applyTheme(t); }, []);

  const value = useMemo(() => ({
    theme, setTheme, presetThemes: PRESET_THEMES,
    moderationEnabled, setModerationEnabled,
    categories, refreshPublic, loadingPublic,
    user, authLoading, login, register, logout, updateProfile, completeGoogleLogin, refreshAuth,
    adminLoggedIn, adminLogin, adminLogout, refreshAdmin,
    favorites, isFav, toggleFavorite,
  }), [
    theme, moderationEnabled, categories, loadingPublic,
    user, authLoading, adminLoggedIn, favorites,
    setTheme, refreshPublic, login, register, logout, updateProfile, completeGoogleLogin, refreshAuth,
    adminLogin, adminLogout, refreshAdmin, isFav, toggleFavorite,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
